/**
 * Client side of the off-thread line.
 *
 * The same two promises SolverRunner makes for the station solvers, for the
 * same reasons: LATEST WINS per channel, so a student who changes the load
 * twice gets the answer to the second question and not a backlog of the
 * first; and an INLINE FALLBACK where a module worker is not to be had, so
 * the panel still works on a locked-down classroom machine at the cost of a
 * hitch rather than of the feature.
 */

import type { ProductMix, ChillerTripResult, ExcursionStudy, OperatingPoint, PmPolicy, PmStudy, Policy, QtimePolicy, QtimeResult } from './scenario'
import { answer, type LineReply, type LineRequest } from '../line.worker'

type Pending = { resolve: (reply: LineReply | null) => void }

export class LineRunner {
  private worker: Worker | null = null
  private nextId = 1
  private readonly pending = new Map<number, Pending>()
  /** The live request id on each channel; an older one resolves to null. */
  private readonly live: Partial<Record<LineRequest['kind'], number>> = {}

  constructor() {
    try {
      this.worker = new Worker(new URL('../line.worker.ts', import.meta.url), { type: 'module' })
      this.worker.onmessage = (e: MessageEvent<LineReply>) => this.settle(e.data)
      this.worker.onerror = () => {
        // Fall back for good: every pending request answers inline.
        this.worker?.terminate()
        this.worker = null
        for (const [id, p] of this.pending) {
          this.pending.delete(id)
          p.resolve(null)
        }
      }
    } catch {
      this.worker = null
    }
  }

  dispose(): void {
    this.worker?.terminate()
    this.worker = null
    for (const p of this.pending.values()) p.resolve(null)
    this.pending.clear()
  }

  /** All three policies on one seed, or null if a newer request superseded it. */
  trip(load: number, restoreH: number, seed: number, mix: ProductMix = 'one'): Promise<Record<Policy, ChillerTripResult> | null> {
    return this.send({ id: this.nextId++, kind: 'trip', load, restoreH, seed, mix }).then((r) => (r && r.kind === 'trip' ? r.results : null))
  }

  curve(loads: number[], seed: number): Promise<OperatingPoint[] | null> {
    return this.send({ id: this.nextId++, kind: 'curve', loads, seed }).then((r) => (r && r.kind === 'curve' ? r.points : null))
  }

  qtime(load: number, windowH: number, seed: number): Promise<Record<QtimePolicy, QtimeResult> | null> {
    return this.send({ id: this.nextId++, kind: 'qtime', load, windowH, seed }).then((r) => (r && r.kind === 'qtime' ? r.results : null))
  }

  excursion(rates: number[], seeds: number[], load: number): Promise<ExcursionStudy[] | null> {
    return this.send({ id: this.nextId++, kind: 'excursion', rates, seeds, load }).then((r) => (r && r.kind === 'excursion' ? r.studies : null))
  }

  pm(seeds: number[], load: number): Promise<Record<PmPolicy, PmStudy> | null> {
    return this.send({ id: this.nextId++, kind: 'pm', seeds, load }).then((r) => (r && r.kind === 'pm' ? r.studies : null))
  }

  private send(req: LineRequest): Promise<LineReply | null> {
    // Supersede whatever was in flight on this channel.
    const old = this.live[req.kind]
    if (old !== undefined) {
      const p = this.pending.get(old)
      if (p) {
        this.pending.delete(old)
        p.resolve(null)
      }
    }
    this.live[req.kind] = req.id
    if (!this.worker) {
      // Inline: the same function, on this thread, after a tick so the
      // caller's "running" state gets painted first.
      return new Promise((resolve) =>
        setTimeout(() => {
          if (this.live[req.kind] !== req.id) return resolve(null)
          try {
            resolve(answer(req))
          } catch (err) {
            resolve({ id: req.id, kind: 'error', message: err instanceof Error ? err.message : String(err) })
          }
        }, 0),
      )
    }
    return new Promise((resolve) => {
      this.pending.set(req.id, { resolve })
      this.worker!.postMessage(req)
    })
  }

  private settle(reply: LineReply): void {
    const p = this.pending.get(reply.id)
    if (!p) return
    this.pending.delete(reply.id)
    p.resolve(reply)
  }
}
